import { createSlice, PayloadAction } from "@reduxjs/toolkit";
interface AIMessageOption {
  label: string;
  value: string;
}

export interface AiJobPostingMessage {
  id?: string;
  role: string;
  text?: string;
  timestamp?: string;
  type?: string;
  options?: AIMessageOption[];
  allow_multiple?: boolean;
  [key: string]: unknown;
}

export interface PreviewData {
  summary?: Record<string, unknown> | null;
  markdown?: string;
  /** Full job description from WebSocket session_completed (job_description field). */
  job_description?: string | null;
  /** Assessment questions from WebSocket session_completed (assessment field). */
  assessment?: unknown[] | string | null;
  /** Custom screening questions (normalized to { question }). */
  custom_questions?: unknown[] | null;
  /** Agency client row id from WebSocket session_completed (top-level client_id). */
  client_id?: string | number | null;
  /** salary_period numeric pk persisted from final_summary (e.g. 1 = Annually). */
  salary_period?: number | string | null;
  /** tolerance_margin_enabled (bool) from final_summary / session_completed. */
  tolerance_margin?: boolean | string | null;
  /** credits (numeric) from final_summary budget allocation. */
  credits?: number | null;
  /** salary_is_unpaid (bool) from session_completed. */
  salary_is_unpaid?: boolean | null;
}

export interface AiJobPostingSessionState {
  sessionID: string;
  conversationHistory: AiJobPostingMessage[];
  previewData: PreviewData | null;
  /**
   * Resume-draft: id of the existing drafted job this session is editing.
   * Persisted so the "Update Job" behaviour survives a mid-session refresh and
   * the session never falls back to creating a duplicate job. Empty = fresh job.
   */
  draftJobId: string;
  loading: boolean;
  messages: any[];
  error: string | null;
}

const initialState: AiJobPostingSessionState = {
  sessionID: "",
  conversationHistory: [],
  previewData: null,
  draftJobId: "",
  messages: [],
  loading: false,
  error: null,
};

const aiJobPostingSessionSlice = createSlice({
  name: "aiJobPosting",
  initialState,
  reducers: {
    setSessionIDOfJobPostingAI: (state, action: PayloadAction<string>) => {
      state.sessionID = action.payload;
    },
    clearSessionID: (state) => {
      state.sessionID = "";
      // The draft link is scoped to the session — drop it whenever the session
      // is cleared so a later fresh session never inherits a stale draft id.
      state.draftJobId = "";
    },
    /** Set when a resume-draft session starts (empty string clears it). */
    setAiDraftJobId: (state, action: PayloadAction<string>) => {
      state.draftJobId = action.payload ?? "";
    },
    setConversationHistory: (
      state,
      action: PayloadAction<AiJobPostingMessage[]>,
    ) => {
      state.conversationHistory = action.payload;
    },
    clearConversationHistory: (state) => {
      state.conversationHistory = [];
    },
    setPreviewData: (
      state,
      action: PayloadAction<PreviewData | null>,
    ) => {
      state.previewData = action.payload;
    },
    clearPreviewData: (state) => {
      state.previewData = null;
    },
  },
});

export const {
  setSessionIDOfJobPostingAI,
  clearSessionID,
  setAiDraftJobId,
  setConversationHistory,
  clearConversationHistory,
  setPreviewData,
  clearPreviewData,
} = aiJobPostingSessionSlice.actions;

export default aiJobPostingSessionSlice.reducer;
